import React, {useState} from 'react';
import {
  Flex,
  Box,
  Table,
  Thead,
  Tbody,
  Button,
  Th,
  Tr,
  Td,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Divider,
  TableContainer,
  useDisclosure,
} from '@chakra-ui/react';
import {CustomInput, SubmitButton, CustomSelect, CustomButton} from '../../General/FormInputs';
import {useForm, useFieldArray} from 'react-hook-form';
import FlexCard from '../../General/FlexCard';
import {FaPlusSquare, FaTrashAlt} from 'react-icons/fa';
import {get, post} from '../../../service/ApiService';
import {OpportunityPlan} from '../../../service/Urls';
import ErrorHelper from '../../../service/ErrorHelper';
import {useClientOpp} from '../../../views/Maintenance/OppPlanMaintenance';
import {isAdmin} from '../../../helpers/localStorage';
import {useMutation, useQueryClient} from 'react-query';
import {useQuery} from 'react-query';

const OppLocation = props => {
  const {client} = useClientOpp(null);
  const [location, setLocation] = useState(null);
  const [toast, setToast] = useState('');

  const queryClient = useQueryClient();

  const {data: clientOpp} = useQuery(
    ['clientOpp', client?.client?.id],
    async () => {
      return get(OpportunityPlan.GET_BY_ID + '/' + client.client.id);
    },
    {enabled: !!client?.client?.id}
  );

  const {data: locations} = useQuery(
    ['locations', clientOpp?.id],
    async () => {
      return get(OpportunityPlan.RECORD + '/' + clientOpp.id + '/locations');
    },
    {
      onSuccess: response => {
        const firstLocation = response?.data?.[0];

        if (!location && firstLocation) {
          setLocation(firstLocation);
          reset(firstLocation);
        }
      },
      enabled: !!clientOpp?.id,
    }
  );

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: {errors},
  } = useForm();

  const {
    fields: aliases,
    append: appendAlias,
    remove: removeAlias,
  } = useFieldArray({
    name: 'aliases',
    control,
  });

  const {mutate: submit, isLoading: isSubmitting} = useMutation(
    data => post(OpportunityPlan.RECORD + '/' + clientOpp.id + '/locations/' + data.id, data),
    {
      onSuccess: result => {
        if (result.error) {
          setToast({response: {error: true}, errorMessage: result.error});
        } else {
          setToast({successMessage: 'Successfully updated location.'});
        }

        queryClient.invalidateQueries(['locations', clientOpp.id]);
      },
      onError: () => {
        setToast({response: {error: true}, errorMessage: 'Error updating location.  Please try again.'});
      },
    }
  );

  const {mutate: update, isLoading: isUpdating} = useMutation(() => post(OpportunityPlan.MISSING_LOCATIONS), {
    onSuccess: result => {
      if (result.error) {
        setToast({response: {error: true}, errorMessage: result.error});
      } else {
        setToast({successMessage: 'Successfully updated locations.'});
      }
      queryClient.invalidateQueries(['locations', clientOpp.id]);
    },
    onError: () => {
      setToast({response: {error: true}, errorMessage: 'Error updating locations.  Please try again.'});
    },
  });

  const {isOpen, onOpen, onClose} = useDisclosure();

  const onLocationSelect = event => {
    const selectedLocation = locations?.data?.find(loc => loc.loId == event.target.value);
    setLocation(selectedLocation);
    reset(selectedLocation);
  };

  const onAddAlias = () => {
    appendAlias({
      nseq: Math.max(...aliases.map(({nseq}) => nseq ?? 0), 0) + 1,
      calias: '',
    });
  };

  const onSubmit = async data => {
    if (!isAdmin()) {
      setToast({response: {error: 1}, errorMessage: 'Invalid Permissions'});
      return;
    }

    submit(data);
  };

  return (
    <Box mr="2">
      <Flex alignItems="flex-end">
        <CustomSelect w="50%" mb="2" pb="2" id="Selector" register={register} onChange={onLocationSelect} errors={errors} name="Select Location">
          {locations?.data?.map(loc => {
            return (
              <option key={loc.id} value={loc.loId}>
                {loc.loId + ' | ' + loc.loName + ' | ' + loc.loRptname}
              </option>
            );
          })}
        </CustomSelect>

        {isAdmin() && (
          <Button ml="2" mb="2" onClick={() => update()} disabled={isUpdating}>
            Update
          </Button>
        )}
      </Flex>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex>
          <FlexCard mr="2">
            <Box>
              <Flex>
                <CustomInput register={register} id="loId" name="Code" errors={errors} isReadOnly={true} w="15%" />
                <CustomInput register={register} id="loName" name="Location Name" isReadOnly={true} errors={errors} w="50%" />
                <CustomInput register={register} id="loRptname" name="Rpt Name" errors={errors} isReadOnly={location ? false : true} />
                <CustomInput register={register} id="loOppname" name="Opp Name" errors={errors} isReadOnly={location ? false : true} />
              </Flex>
            </Box>
          </FlexCard>

          <FlexCard w="35%">
            <Flex w="100%">
              <CustomInput
                register={register}
                id="loTarget"
                name="Target Rev/Call"
                requireInt={true}
                type="number"
                errors={errors}
                isReadOnly={location ? false : true}
                w="30%"
              />
              <CustomInput
                register={register}
                id="loArbal"
                name="A/R Balance"
                requireInt={true}
                type="number"
                errors={errors}
                isReadOnly={location ? false : true}
                w="30%"
              />

              {location ? (
                <CustomButton
                  w="40%"
                  mt="5"
                  name="View Aliases"
                  onClick={() => {
                    onOpen();
                  }}
                />
              ) : (
                ''
              )}
            </Flex>
          </FlexCard>
        </Flex>
        {isAdmin() && <SubmitButton name="Save" disabled={isSubmitting} />}
      </form>

      <Modal blockScrollOnMount={false} size={'4xl'} isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <form>
          <ModalContent>
            <ModalHeader id="header">Aliases</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Divider mt="5" colorScheme={'mint'} width={{base: '100%'}} />
              <TableContainer width={{base: '100%'}}>
                <Table variant="striped" border="solid 2px #0c5f78" colorScheme="mint" size={'md'}>
                  <Thead>
                    <Tr border="solid 2px #0c5f78">
                      <Th>Alias Number</Th>
                      <Th>Alias</Th>
                      {isAdmin() && (
                        <Th>
                          <IconButton colorScheme="tertiary" title="Add" size="md" icon={<FaPlusSquare />} onClick={onAddAlias} />
                        </Th>
                      )}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {aliases == []
                      ? []
                      : aliases.map((alias, idx) => {
                          const calasId = `aliases.${idx}.calias`;

                          const aliasErrors = errors?.aliases?.[idx] ?? {};
                          return (
                            <Tr key={idx}>
                              <Td>{alias.nseq}</Td>
                              <Td>
                                <CustomInput id={calasId} errorId="calias" register={register} errors={aliasErrors} name="Alias" />
                              </Td>
                              {isAdmin() && (
                                <Td>
                                  <IconButton
                                    colorScheme="tertiary"
                                    title="Delete"
                                    size="md"
                                    icon={<FaTrashAlt />}
                                    onClick={() => removeAlias(idx)}
                                  />
                                </Td>
                              )}
                            </Tr>
                          );
                        })}
                  </Tbody>
                </Table>
              </TableContainer>
            </ModalBody>
          </ModalContent>
        </form>
      </Modal>
      <ErrorHelper toastResponse={toast} />
    </Box>
  );
};

export default OppLocation;
